import { useState, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './AffiliateApplication.module.css';

type AffiliateData = {
  name?: string;
  email?: string;
  code?: string;
};

type LocationState = {
  fromSignup?: boolean;
  affiliateData?: AffiliateData;
};

export default function AffiliateApplication() {
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const [fromSignup] = useState(() => Boolean(locationState?.fromSignup));
  const [affiliateData] = useState<AffiliateData | null>(() => locationState?.affiliateData ?? null);
  const [linkCopied, setLinkCopied] = useState(false);
  const linkRef = useRef<HTMLInputElement | null>(null);
  const { currentUser } = useAuth();
  const hasAffiliateCode = Boolean(affiliateData?.code);
  const activationPending = affiliateData && !hasAffiliateCode;

  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <div className={styles.content}>
        <div className={styles.contentContainer}>
          <h1 className={styles.title}>Success!</h1>

          <div className={styles.successMessage}>
            <p className={styles.greeting}>
              Welcome to MyPeopleDatabase, {currentUser?.email || 'New Affiliate'}!
            </p>

            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Your affiliate journey starts now</h2>
              <p>
                {activationPending
                  ? 'We’re finalizing your affiliate tools. Keep an eye on your inbox—we’ll send your referral link as soon as it’s ready.'
                  : 'Your affiliate tools are live. Share your referral link to start earning commissions right away.'}
              </p>

              {affiliateData && (
                <div className={styles.affiliateInfo}>
                  <h3>Your Affiliate Information</h3>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Name:</span>
                    <span className={styles.value}>{affiliateData.name}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Email:</span>
                    <span className={styles.value}>{affiliateData.email}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Affiliate Code:</span>
                    <span className={styles.value}>
                      {hasAffiliateCode
                        ? affiliateData.code
                        : 'Pending – watch for your activation email'}
                    </span>
                  </div>
                </div>
              )}

              {affiliateData && hasAffiliateCode && (
                <div className={styles.affiliateLinkSection}>
                  <h3>Your Affiliate Link</h3>
                  <p>Share this link with potential customers to earn commissions:</p>

                  <div className={styles.copyLinkContainer}>
                    <input
                      ref={linkRef}
                      type="text"
                      readOnly
                      value={`${window.location.origin}/buy?ref=${affiliateData.code}`}
                      className={styles.linkInput}
                    />
                    <button
                      className={styles.copyButton}
                      onClick={() => {
                        if (linkRef.current) {
                          linkRef.current.select();
                          document.execCommand('copy');
                          setLinkCopied(true);
                          setTimeout(() => setLinkCopied(false), 3000);
                        }
                      }}
                    >
                      {linkCopied ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </div>
              )}

              {activationPending && (
                <div className={styles.activationNotice}>
                  <h3>What happens next?</h3>
                  <ul>
                    <li>We’ll email you as soon as your unique referral link is created.</li>
                    <li>Finish your affiliate profile so payouts can be sent without delays.</li>
                    <li>
                      Have questions? Reply to the welcome email or contact
                      support@mypeopledatabase.com.
                    </li>
                  </ul>
                </div>
              )}

              <h3 className={styles.nextSteps}>Your immediate next steps</h3>
              <ul className={styles.stepsList}>
                <li>
                  <span className={styles.stepNumber}>1</span>
                  <div className={styles.stepContent}>
                    <strong>Complete your profile</strong>
                    <p>
                      Add your contact information and payout details so commissions can be issued
                      without delay.
                    </p>
                    <Link to="/affiliate-profile" className={styles.actionButton}>
                      Complete Profile
                    </Link>
                  </div>
                </li>

                <li>
                  <span className={styles.stepNumber}>2</span>
                  <div className={styles.stepContent}>
                    <strong>Explore your dashboard</strong>
                    <p>
                      {activationPending
                        ? 'Preview where your referral stats will appear and familiarize yourself with the tools.'
                        : 'Track your stats, commissions, and find additional marketing resources.'}
                    </p>
                    <Link to="/dashboard" className={styles.actionButton}>
                      Go to Dashboard
                    </Link>
                  </div>
                </li>

                <li>
                  <span className={styles.stepNumber}>3</span>
                  <div className={styles.stepContent}>
                    <strong>Learn about MyPeopleDatabase</strong>
                    <p>
                      Discover all the features that make MyPeopleDatabase special, including how it
                      handles preserving records of those who have passed away while keeping your
                      active contact list current.
                    </p>
                    <Link to="/features" className={styles.actionButton}>
                      Explore Features
                    </Link>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
